import React, { useEffect, useRef, useState } from 'react';
import { getClient } from '@graphql/graphql-client/graphClient';
import { useQueryClient } from 'react-query';
import {
    useContactableCourseMemberGroupTypesQuery,
    useAccountUsersQuery,
    useUpdateConversationMutation,
    useContactableUsersQuery,
} from '@graphql/generated-graphql';
import { useUniversalModalDispatch } from '@components/context/UniversalModalContext';
import { useOnClickOutside } from '@hooks/useOnClickOutside';
import { Button } from '@skipper/ui';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SelectPeopleDropdown } from '@components/modals/chatModal/ChatComponents';
import { SimpleSpinner } from '@components/common/loading/SimpleSpinner';

export const EditMembersModal = ({ conversation }: { conversation: any }) => {
    const { toggle, dispatchNotification } = useUniversalModalDispatch();
    const queryClient = useQueryClient();
    const [chatMembers, setChatMembers] = useState(conversation?.participants);
    const [selectedCourses, setSelectedCourses] = useState<any[]>([]);
    const [selectedSet, setSelectedSet] = useState([]);

    const elementRef = useRef<HTMLDivElement | null>(null);
    useOnClickOutside(elementRef, () => {
        toggle(false);
    });

    const { data, isLoading } = useContactableUsersQuery(
        getClient('communication'),
        {},
        { refetchOnWindowFocus: false },
    );

    const { data: userData } = useAccountUsersQuery(getClient('users'), {
        includeInactive: true,
    });

    const { data: coursesData, isLoading: coursesLoading } =
        useContactableCourseMemberGroupTypesQuery(getClient('communication'));

    useEffect(() => {
        if (coursesData?.contactableCourseMemberGroupTypes) {
            let list = [];
            conversation?.participatingUsers?.map((member) => {
                list.push({ type: 'user', element: member });
            });
            conversation?.participatingCourseMemberGroups?.map((course) => {
                list.push({
                    type: 'course',
                    element: {
                        courseId: course.course.id,
                        selectedMemberGroupTypes: course.memberGroupTypes,
                        iconColor: course.course.iconColor,
                        displayMemberGroupTypes: coursesData.contactableCourseMemberGroupTypes.find(
                            (el) => el.course.id === course.course.id,
                        ).memberGroupTypes,
                        title: course.course.title,
                    },
                });
            });
            setSelectedSet(list);
        }
    }, [coursesData, conversation]);

    const childUsers = data?.contactableUsers?.filter(
        (user) =>
            user?.accountRole.roleType === 'child' &&
            !userData?.accountUsers?.find((u) => u.id === user.id),
    );

    const adultUsers = data?.contactableUsers?.filter(
        (user) =>
            user?.accountRole.roleType === 'adult' &&
            !userData?.accountUsers?.find((u) => u.id === user.id),
    );

    const courseMemberGroupTypes = coursesData?.contactableCourseMemberGroupTypes;

    const updateChat = useUpdateConversationMutation(getClient('communication'), {
        onSettled: async (data) => {
            if (data?.updateConversation) {
                toggle(false);
                queryClient.invalidateQueries('conversationsList');
                queryClient.invalidateQueries(['conversationMessages', { id: conversation.id }]);
                dispatchNotification({
                    title: 'Chat Members updated successfully',
                    text: '',
                    type: 'success',
                });
            } else {
                dispatchNotification({
                    title: 'Update Chat Members Error',
                    text: 'Something went wrong',
                    type: 'danger',
                });
            }
        },
    });

    return (
        <div className="fixed inset-0 overflow-y-auto flex h-full">
            <div className="relative flex-grow flex-1 flex justify-center items-center py-2">
                <div
                    className="relative bg-white rounded-md py-5 w-[370px] md:w-[550px] font-medium text-sm"
                    ref={elementRef}>
                    <div className="px-5">
                        <div className="flex justify-between items-center">
                            <h6 className="font-bold">Edit chat members</h6>
                            <FontAwesomeIcon
                                icon={faXmark}
                                size="lg"
                                className="cursor-pointer"
                                onClick={() => toggle(false)}
                            />
                        </div>
                        {isLoading || coursesLoading ? (
                            <div className="mt-16">
                                <SimpleSpinner className="text-gray-500" />
                            </div>
                        ) : (
                            <SelectPeopleDropdown
                                familyData={userData?.accountUsers}
                                childUsers={childUsers}
                                adultUsers={adultUsers}
                                selectedUsers={chatMembers}
                                selectedSet={selectedSet}
                                setSelectedSet={setSelectedSet}
                                setSelectedUsers={setChatMembers}
                                courseMemberGroupTypes={courseMemberGroupTypes}
                                selectedCourses={selectedCourses}
                                setSelectedCourses={setSelectedCourses}
                            />
                        )}
                    </div>
                    <div className="px-5 mt-16 border-t">
                        <Button
                            variant="magenta"
                            size="medium"
                            className="mt-4 w-full"
                            disabled={selectedSet.length === 0 || isLoading || coursesLoading}
                            onClick={() =>
                                updateChat.mutate({
                                    id: conversation?.id,
                                    participatingUserIds: selectedSet
                                        .filter((el) => el.type === 'user')
                                        .map((el) => el.element.id),
                                    participatingCourses: selectedSet
                                        .filter((el) => el.type === 'course')
                                        .map((el) => {
                                            return {
                                                courseId: el.element.courseId,
                                                memberGroupTypes:
                                                    el.element.selectedMemberGroupTypes,
                                            };
                                        }),
                                    iconFileEntryId: null,
                                    title: conversation?.title,
                                })
                            }>
                            Save
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
